import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Tabs,
  Tab,
  TableSortLabel,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Person,
  PersonAdd,
  Email,
  Work,
  Security,
  LockReset,
  Visibility,
  VisibilityOff,
  Search,
} from '@mui/icons-material';
import { apiClient } from '../services/api';
import { User, Department, Campaign } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { CampaignFilterService } from '../services/campaignFilterService';
import { TimezoneService } from '../services/timezoneService';
import { filterUsersVisibleToViewer, canAssignUserRole } from '../utils/userVisibilityPolicy';

interface UserFormData {
  employeeId: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  displayName: string;
  role: User['role'];
  departmentId: string;
  workLocation: User['workLocation'];
  timezone: string;
  assignedCampaigns: string[];
  isActive: boolean;
  mustChangePassword: boolean;
}

const UsersManagementPage: React.FC = () => {
  const { user } = useAuth();
  
  // Role-based access control
  const isSupervisorOrAbove = user?.role && ['supervisor', 'manager', 'administrator', 'super_user'].includes(user.role);
  const isManagerOrAbove = user?.role && ['manager', 'administrator', 'super_user'].includes(user.role);
  const isAdministratorOrAbove = user?.role && ['administrator', 'super_user'].includes(user.role);
  
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    employeeId: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    displayName: '',
    role: 'agent',
    departmentId: '',
    workLocation: 'on_site',
    timezone: 'UTC',
    assignedCampaigns: [],
    isActive: true,
    mustChangePassword: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [passwordTimeout, setPasswordTimeout] = useState<NodeJS.Timeout | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<0 | 1>(0); // 0 = Active, 1 = Inactive
  const [sortBy, setSortBy] = useState<
    'employeeId' | 'name' | 'email' | 'role' | 'department' | 'workLocation' | 'status' | 'passwordChange'
  >('employeeId');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Restore employee (Inactive → Active): chooser → reactivate confirm | rehire form
  const [restoreTargetUser, setRestoreTargetUser] = useState<User | null>(null);
  const [restoreChooserOpen, setRestoreChooserOpen] = useState(false);
  const [reactivateConfirmOpen, setReactivateConfirmOpen] = useState(false);
  const [rehireFormOpen, setRehireFormOpen] = useState(false);
  const [rehireHireDate, setRehireHireDate] = useState('');
  const [rehireMustChangePassword, setRehireMustChangePassword] = useState(true);
  const [restoreSubmitting, setRestoreSubmitting] = useState(false);
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);

  const roles = [
    'super_user',
    'administrator',
    'manager',
    'supervisor',
    'team_lead',
    'agent',
    'other'
  ];

  const assignableRoles = React.useMemo(
    () => roles.filter((role) => canAssignUserRole(user?.role, role)),
    [user?.role],
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, departmentsData, campaignsData] = await Promise.all([
        apiClient.users.getAll(),
        apiClient.departments.getAll(),
        apiClient.campaigns.getAll(),
      ]);
      
      setUsers(usersData.data.data);
      
      // Filter departments based on user role
      let accessibleDepartments = departmentsData.data.data;
      if (user && ['team_lead', 'supervisor'].includes(user.role)) {
        // Team leads and supervisors can only see their own department
        accessibleDepartments = departmentsData.data.data.filter(
          dept => dept.id === user.departmentId
        );
      }
      setDepartments(accessibleDepartments);
      
      // Filter campaigns based on user role and assigned campaigns
      let accessibleCampaigns = campaignsData.data.data;
      if (user) {
        accessibleCampaigns = CampaignFilterService.getUserAccessibleCampaigns(user, campaignsData.data.data);
      }
      setCampaigns(accessibleCampaigns);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        employeeId: user.employeeId,
        email: user.email,
        password: '',
        firstName: user.firstName,
        lastName: user.lastName,
        displayName: user.displayName || `${user.firstName} ${user.lastName}`,
        role: user.role,
        departmentId: user.departmentId,
        workLocation: user.workLocation,
        timezone: user.timezone || 'UTC',
        assignedCampaigns: user.assignedCampaigns ? JSON.parse(user.assignedCampaigns) : [],
        isActive: user.isActive,
        mustChangePassword: user.mustChangePassword,
      });
    } else {
      setEditingUser(null);
      setFormData({
        employeeId: '',
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        displayName: '',
        role: 'agent',
        departmentId: '',
        workLocation: 'on_site',
        timezone: 'UTC',
        assignedCampaigns: [],
        isActive: true,
        mustChangePassword: false,
      });
    }
    setDialogOpen(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setError(null);
    setSuccess(null);
    setSubmitting(false);
  };

  const closeRestoreFlow = () => {
    setRestoreChooserOpen(false);
    setReactivateConfirmOpen(false);
    setRehireFormOpen(false);
    setRestoreTargetUser(null);
    setRehireHireDate('');
    setRehireMustChangePassword(true);
    setRestoreSubmitting(false);
  };

  const openRestoreChooser = (target: User) => {
    setRestoreTargetUser(target);
    setRestoreChooserOpen(true);
    setReactivateConfirmOpen(false);
    setRehireFormOpen(false);
    setError(null);
  };

  const handleStatusSelectChange = (value: string) => {
    const nextActive = value === 'active';
    if (editingUser && !formData.isActive && nextActive) {
      openRestoreChooser(editingUser);
      return;
    }
    if (editingUser && formData.isActive && !nextActive) {
      setDeactivateConfirmOpen(true);
      return;
    }
    setFormData({ ...formData, isActive: nextActive });
  };

  const confirmDeactivate = () => {
    setFormData({ ...formData, isActive: false });
    setDeactivateConfirmOpen(false);
  };

  const applyRestoredUser = (restored: User) => {
    setUsers((prev) => prev.map((u) => (u.id === restored.id ? { ...u, ...restored } : u)));
    if (editingUser?.id === restored.id) {
      setEditingUser({ ...editingUser, ...restored });
      setFormData((prev) => ({
        ...prev,
        isActive: true,
        mustChangePassword: restored.mustChangePassword ?? prev.mustChangePassword,
      }));
    }
  };

  const handleConfirmReactivate = async () => {
    if (!restoreTargetUser) return;
    setRestoreSubmitting(true);
    setError(null);
    try {
      const res = await apiClient.users.reactivate(restoreTargetUser.id);
      applyRestoredUser(res.data.data);
      setSuccess(res.data.message || 'User reactivated. TEKS login/access restored.');
      closeRestoreFlow();
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reactivate user');
    } finally {
      setRestoreSubmitting(false);
    }
  };

  const handleConfirmRehire = async () => {
    if (!restoreTargetUser) return;
    if (!rehireHireDate) {
      setError('New hire date is required for rehire');
      return;
    }
    setRestoreSubmitting(true);
    setError(null);
    try {
      const res = await apiClient.users.rehire(restoreTargetUser.id, {
        hireDate: rehireHireDate,
        mustChangePassword: rehireMustChangePassword,
      });
      applyRestoredUser(res.data.data);
      setSuccess(res.data.message || 'User rehired. TEKS login/access restored.');
      closeRestoreFlow();
      handleCloseDialog();
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to rehire user');
    } finally {
      setRestoreSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // Prepare data for API - convert assignedCampaigns array to JSON string
      // If displayName is empty, backend will default to firstName + lastName
      const apiData = {
        ...formData,
        displayName: formData.displayName.trim() || undefined, // Empty string becomes undefined so backend can default it
        assignedCampaigns: JSON.stringify(formData.assignedCampaigns)
      };

      if (editingUser) {
        await apiClient.users.update(editingUser.id, apiData);
        setSuccess('User updated successfully');
      } else {
        await apiClient.users.create(apiData);
        setSuccess('User created successfully');
      }

      // Close dialog immediately and reload data in background
      handleCloseDialog();
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await apiClient.users.delete(userId);
        setSuccess('User deleted successfully');
        await loadData();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete user');
      }
    }
  };

  const getRoleColor = (role: string) => {
    const colors: { [key: string]: string } = {
      super_user: 'error',
      administrator: 'warning',
      manager: 'info',
      supervisor: 'primary',
      team_lead: 'secondary',
      agent: 'success',
      other: 'default',
    };
    return colors[role] || 'default';
  };

  const getDepartmentName = (departmentId: string) => {
    const department = departments.find(d => d.id === departmentId);
    return department?.name || 'Unknown';
  };

  const getDisplayName = (user: User) => {
    return user.displayName || `${user.firstName} ${user.lastName}`;
  };

  const getSortValue = (user: User, key: typeof sortBy) => {
    switch (key) {
      case 'employeeId':
        return user.employeeId || '';
      case 'name':
        return getDisplayName(user);
      case 'email':
        return user.email || '';
      case 'role':
        return user.role || '';
      case 'department':
        return getDepartmentName(user.departmentId);
      case 'workLocation':
        return user.workLocation || '';
      case 'status':
        return user.isActive ? 'active' : 'inactive';
      case 'passwordChange':
        return user.mustChangePassword ? 'required' : 'not_required';
      default:
        return '';
    }
  };

  const handleSortChange = (key: typeof sortBy) => {
    if (sortBy === key) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(key);
    setSortDirection('asc');
  };

  const visibleUsers = React.useMemo(
    () => filterUsersVisibleToViewer(users, user?.role),
    [users, user?.role],
  );

  // Filter users based on search term and active status
  const filteredUsers = visibleUsers.filter(user => {
    // First filter by active/inactive status based on selected tab
    const matchesStatus = activeTab === 0 ? user.isActive : !user.isActive;
    if (!matchesStatus) return false;
    
    // Then filter by search term
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const displayName = getDisplayName(user);
    return (
      user.employeeId.toLowerCase().includes(searchLower) ||
      user.firstName.toLowerCase().includes(searchLower) ||
      user.lastName.toLowerCase().includes(searchLower) ||
      displayName.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower) ||
      user.role.toLowerCase().includes(searchLower) ||
      getDepartmentName(user.departmentId).toLowerCase().includes(searchLower)
    );
  });

  const sortedUsers = [...filteredUsers]
    .map((user, index) => ({ user, index }))
    .sort((a, b) => {
      const aValue = getSortValue(a.user, sortBy);
      const bValue = getSortValue(b.user, sortBy);
      const comparison = aValue.localeCompare(bValue, undefined, { sensitivity: 'base', numeric: true });
      if (comparison !== 0) {
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      return a.index - b.index;
    })
    .map(({ user }) => user);

  // Calculate counts for each tab
  const activeUsers = visibleUsers.filter(user => user.isActive);
  const inactiveUsers = visibleUsers.filter(user => !user.isActive);
  
  const filteredActiveUsers = activeUsers.filter(user => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const displayName = getDisplayName(user);
    return (
      user.employeeId.toLowerCase().includes(searchLower) ||
      user.firstName.toLowerCase().includes(searchLower) ||
      user.lastName.toLowerCase().includes(searchLower) ||
      displayName.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower) ||
      user.role.toLowerCase().includes(searchLower) ||
      getDepartmentName(user.departmentId).toLowerCase().includes(searchLower)
    );
  });
  
  const filteredInactiveUsers = inactiveUsers.filter(user => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const displayName = getDisplayName(user);
    return (
      user.employeeId.toLowerCase().includes(searchLower) ||
      user.firstName.toLowerCase().includes(searchLower) ||
      user.lastName.toLowerCase().includes(searchLower) ||
      displayName.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower) ||
      user.role.toLowerCase().includes(searchLower) ||
      getDepartmentName(user.departmentId).toLowerCase().includes(searchLower)
    );
  });

  const handleOpenResetPasswordDialog = (user: User) => {
    setResetPasswordUser(user);
    setNewPassword('');
    setResetPasswordDialogOpen(true);
    setError(null);
  };

  const handleCloseResetPasswordDialog = () => {
    setResetPasswordDialogOpen(false);
    setResetPasswordUser(null);
    setNewPassword('');
    setError(null);
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser || !newPassword) {
      setError('Please enter a new password');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      setError(null);
      await apiClient.users.resetPassword(resetPasswordUser.id, newPassword);
      setSuccess(`Password reset successfully for ${resetPasswordUser.firstName} ${resetPasswordUser.lastName}. They will be required to change it on next login.`);
      handleCloseResetPasswordDialog();
      // Refresh users list to show updated mustChangePassword status
      loadData();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to reset password');
    }
  };

  const handleTogglePasswordVisibility = () => {
    // Clear existing timeout
    if (passwordTimeout) {
      clearTimeout(passwordTimeout);
    }
    
    setShowPassword(!showPassword);
    
    // Auto-hide after 5 seconds
    const timeout = setTimeout(() => {
      setShowPassword(false);
    }, 5000);
    
    setPasswordTimeout(timeout);
  };

  const handleToggleResetPasswordVisibility = () => {
    setShowResetPassword(!showResetPassword);
  };

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (passwordTimeout) {
        clearTimeout(passwordTimeout);
      }
    };
  }, [passwordTimeout]);

  const stickyActionHeaderSx = {
    position: 'sticky',
    right: 0,
    zIndex: 4,
    bgcolor: 'background.paper',
    boxShadow: '-4px 0 8px -4px rgba(0,0,0,0.12)',
    whiteSpace: 'nowrap',
    minWidth: 148,
  } as const;

  const stickyActionCellSx = {
    position: 'sticky',
    right: 0,
    zIndex: 1,
    bgcolor: 'background.paper',
    boxShadow: '-4px 0 8px -4px rgba(0,0,0,0.08)',
    whiteSpace: 'nowrap',
    minWidth: 148,
  } as const;

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3 }, width: '100%' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3 }, width: '100%' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
          Users Management
        </Typography>
        {/* Add User button: Only managers and above (supervisors have users.read/update but not create) */}
        {isManagerOrAbove && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add User
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Search Box */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search users by name, email, employee ID, role, or department..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary">
            {activeTab === 0 
              ? `${filteredActiveUsers.length} of ${activeUsers.length} active users`
              : `${filteredInactiveUsers.length} of ${inactiveUsers.length} inactive users`
            }
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue as 0 | 1)}
            sx={{ mb: 2 }}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab 
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip 
                    label={filteredActiveUsers.length} 
                    size="small" 
                    color="success" 
                    sx={{ height: 20, minWidth: 30 }}
                  />
                  Active Users
                </Box>
              } 
            />
            <Tab 
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip 
                    label={filteredInactiveUsers.length} 
                    size="small" 
                    color="default" 
                    sx={{ height: 20, minWidth: 30 }}
                  />
                  Inactive Users
                </Box>
              } 
            />
          </Tabs>
          
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Scroll horizontally to view Password Change and Actions columns.
          </Typography>

          <TableContainer
            component={Paper}
            sx={{
              overflowX: 'auto',
              maxWidth: '100%',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Table size="small" stickyHeader sx={{ minWidth: 1320 }}>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sortBy === 'employeeId' ? sortDirection : false} sx={{ minWidth: 110 }}>
                    <TableSortLabel
                      active={sortBy === 'employeeId'}
                      direction={sortBy === 'employeeId' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('employeeId')}
                    >
                      Employee ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ minWidth: 108 }}>Hire Date</TableCell>
                  <TableCell sortDirection={sortBy === 'name' ? sortDirection : false} sx={{ minWidth: 160 }}>
                    <TableSortLabel
                      active={sortBy === 'name'}
                      direction={sortBy === 'name' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'email' ? sortDirection : false} sx={{ minWidth: 240 }}>
                    <TableSortLabel
                      active={sortBy === 'email'}
                      direction={sortBy === 'email' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('email')}
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'role' ? sortDirection : false} sx={{ minWidth: 130 }}>
                    <TableSortLabel
                      active={sortBy === 'role'}
                      direction={sortBy === 'role' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('role')}
                    >
                      Role
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'department' ? sortDirection : false} sx={{ minWidth: 190 }}>
                    <TableSortLabel
                      active={sortBy === 'department'}
                      direction={sortBy === 'department' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('department')}
                    >
                      Department
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'workLocation' ? sortDirection : false} sx={{ minWidth: 130 }}>
                    <TableSortLabel
                      active={sortBy === 'workLocation'}
                      direction={sortBy === 'workLocation' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('workLocation')}
                    >
                      Work Location
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'status' ? sortDirection : false} sx={{ minWidth: 100 }}>
                    <TableSortLabel
                      active={sortBy === 'status'}
                      direction={sortBy === 'status' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'passwordChange' ? sortDirection : false} sx={{ minWidth: 140 }}>
                    <TableSortLabel
                      active={sortBy === 'passwordChange'}
                      direction={sortBy === 'passwordChange' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('passwordChange')}
                    >
                      Password Change
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={stickyActionHeaderSx}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.employeeId}</TableCell>
                    <TableCell>{user.hireDate || 'N/A'}</TableCell>
                    <TableCell>
                      {user.displayName || `${user.firstName} ${user.lastName}`}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Email sx={{ mr: 1, fontSize: 16 }} />
                        {user.email}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.role.replace('_', ' ').toUpperCase()}
                        color={getRoleColor(user.role) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Work sx={{ mr: 1, fontSize: 16 }} />
                        {getDepartmentName(user.departmentId)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.workLocation.replace('_', ' ').toUpperCase()}
                        color={
                          user.workLocation === 'on_site' ? 'primary' :
                          user.workLocation === 'remote' ? 'secondary' : 'default'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.isActive ? 'Active' : 'Inactive'}
                        color={user.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.mustChangePassword ? 'Required' : 'Not Required'}
                        color={user.mustChangePassword ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={stickyActionCellSx}>
                      <Box display="flex" alignItems="center" gap={0.25}>
                      {/* Edit button: Supervisors have users.update permission, so they can edit */}
                      {isSupervisorOrAbove && (
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(user)}
                          color="primary"
                          title="Edit User"
                        >
                          <Edit />
                        </IconButton>
                      )}
                      {/* Inactive tab: restore TEKS access via Reactivate or Rehire */}
                      {isSupervisorOrAbove && !user.isActive && (
                        <IconButton
                          size="small"
                          onClick={() => openRestoreChooser(user)}
                          color="success"
                          title="Rehire…"
                        >
                          <PersonAdd />
                        </IconButton>
                      )}
                      {/* Reset Password: Available for supervisors and above */}
                      {isSupervisorOrAbove && (
                        <IconButton
                          size="small"
                          onClick={() => handleOpenResetPasswordDialog(user)}
                          color="warning"
                          title="Reset Password"
                        >
                          <LockReset />
                        </IconButton>
                      )}
                      {/* Delete button: Only administrators and above */}
                      {isAdministratorOrAbove && (
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(user.id)}
                          color="error"
                          title="Delete User"
                        >
                          <Delete />
                        </IconButton>
                      )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit User Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 1 }}>
              <TextField
                label="Employee ID"
                value={formData.employeeId}
                onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Display Name"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                fullWidth
                helperText="How the name will appear in reports. Defaults to First Name + Last Name if empty."
                inputProps={{ maxLength: 200 }}
              />
              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                    onChange={(e) => {
                    const newRole = e.target.value as User['role'];
                    setFormData({ 
                      ...formData, 
                      role: newRole,
                      // Clear assigned campaigns if role is agent, other, manager, administrator, or super_user
                      assignedCampaigns: (newRole === 'agent' || newRole === 'other' || 
                                        newRole === 'manager' || newRole === 'administrator' || 
                                        newRole === 'super_user') ? [] : formData.assignedCampaigns
                    });
                  }}
                  label="Role"
                >
                  {assignableRoles.map((role) => (
                    <MenuItem key={role} value={role}>
                      <Box display="flex" alignItems="center">
                        <Security sx={{ mr: 1, fontSize: 16 }} />
                        {role.replace('_', ' ').toUpperCase()}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel>Department</InputLabel>
                <Select
                  value={formData.departmentId}
                  onChange={(e) => {
                    const newDepartmentId = e.target.value;
                    setFormData({ 
                      ...formData, 
                      departmentId: newDepartmentId,
                      // Clear assigned campaigns when department changes since campaigns will be different
                      assignedCampaigns: []
                    });
                  }}
                  label="Department"
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel>Work Location</InputLabel>
                <Select
                  value={formData.workLocation}
                  onChange={(e) => setFormData({ ...formData, workLocation: e.target.value as User['workLocation'] })}
                  label="Work Location"
                >
                  <MenuItem value="on_site">
                    <Box display="flex" alignItems="center">
                      <Work sx={{ mr: 1, fontSize: 16 }} />
                      On Site
                    </Box>
                  </MenuItem>
                  <MenuItem value="remote">
                    <Box display="flex" alignItems="center">
                      <Work sx={{ mr: 1, fontSize: 16 }} />
                      Remote
                    </Box>
                  </MenuItem>
                  <MenuItem value="hybrid">
                    <Box display="flex" alignItems="center">
                      <Work sx={{ mr: 1, fontSize: 16 }} />
                      Hybrid
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  label="Timezone"
                >
                  {TimezoneService.getCommonTimezones().map((tz) => (
                    <MenuItem key={tz.timezone} value={tz.timezone}>
                      {tz.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Campaign Selection - Only show for Team Lead and Supervisor roles */}
              {(formData.role === 'team_lead' || formData.role === 'supervisor') && (
                <FormControl fullWidth>
                  <InputLabel>Assigned Campaigns</InputLabel>
                  <Select
                    multiple
                    value={formData.assignedCampaigns}
                    onChange={(e) => setFormData({ ...formData, assignedCampaigns: e.target.value as string[] })}
                    label="Assigned Campaigns"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((value) => {
                          const campaign = campaigns.find(c => c.id === value);
                          return (
                            <Chip 
                              key={value} 
                              label={campaign?.name || value} 
                              size="small" 
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {campaigns
                      .filter(campaign => campaign.departmentId === formData.departmentId)
                      .map((campaign) => (
                        <MenuItem key={campaign.id} value={campaign.id}>
                          {campaign.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
              {!editingUser && (
                <TextField
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => handleStatusSelectChange(String(e.target.value))}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
              {editingUser && (
                <TextField
                  label="Hire Date"
                  value={editingUser.hireDate || 'N/A'}
                  fullWidth
                  disabled
                  helperText="Read-only from HR system"
                />
              )}
            </Box>
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.mustChangePassword}
                    onChange={(e) => setFormData({ ...formData, mustChangePassword: e.target.checked })}
                  />
                }
                label="User must change password on first login"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : null}
            >
              {submitting ? 'Saving...' : (editingUser ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Deactivate confirm — future schedules are purged on Update */}
      <Dialog open={deactivateConfirmOpen} onClose={() => setDeactivateConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set Inactive?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Setting this employee to Inactive will delete their future schedules when you click Update.
            Past schedules and history are kept.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeactivate} variant="contained" color="warning">
            Set Inactive
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore employee — intent chooser (both options restore TEKS login/access) */}
      <Dialog open={restoreChooserOpen} onClose={closeRestoreFlow} maxWidth="sm" fullWidth>
        <DialogTitle>Restore employee</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            How should this person become active again? Both options restore TEKS login/access.
          </Typography>
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Button
              variant="outlined"
              fullWidth
              sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1.5 }}
              onClick={() => {
                setRestoreChooserOpen(false);
                setReactivateConfirmOpen(true);
              }}
            >
              <Box>
                <Typography fontWeight={600}>Reactivate</Typography>
                <Typography variant="body2" color="text.secondary">
                  Same employment. Keep hire date and history. Accidental deactivate / temporary hold.
                  Restores TEKS login/access.
                </Typography>
              </Box>
            </Button>
            <Button
              variant="outlined"
              fullWidth
              sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1.5 }}
              onClick={() => {
                const today = new Date().toISOString().slice(0, 10);
                setRehireHireDate(today);
                setRehireMustChangePassword(true);
                setRestoreChooserOpen(false);
                setRehireFormOpen(true);
              }}
            >
              <Box>
                <Typography fontWeight={600}>Rehire</Typography>
                <Typography variant="body2" color="text.secondary">
                  New employment spell. Requires a new hire/return date. Restores TEKS login/access
                  and updates hire date.
                </Typography>
              </Box>
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRestoreFlow}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Reactivate confirm */}
      <Dialog open={reactivateConfirmOpen} onClose={closeRestoreFlow} maxWidth="sm" fullWidth>
        <DialogTitle>Reactivate</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1 }}>
            {restoreTargetUser
              ? `${restoreTargetUser.displayName || `${restoreTargetUser.firstName} ${restoreTargetUser.lastName}`} will be set to Active.`
              : 'This employee will be set to Active.'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Hire date stays{' '}
            <strong>{restoreTargetUser?.hireDate || 'N/A'}</strong> (read-only).
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            TEKS login/access will be restored. Future schedules were cleared when deactivated — re-add if needed.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRestoreFlow} disabled={restoreSubmitting}>Cancel</Button>
          <Button
            onClick={handleConfirmReactivate}
            variant="contained"
            disabled={restoreSubmitting}
            startIcon={restoreSubmitting ? <CircularProgress size={18} /> : null}
          >
            {restoreSubmitting ? 'Reactivating…' : 'Reactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rehire form */}
      <Dialog open={rehireFormOpen} onClose={closeRestoreFlow} maxWidth="sm" fullWidth>
        <DialogTitle>Rehire</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={
                restoreTargetUser
                  ? restoreTargetUser.displayName ||
                    `${restoreTargetUser.firstName} ${restoreTargetUser.lastName}`
                  : ''
              }
              fullWidth
              disabled
            />
            <TextField
              label="Employee ID"
              value={restoreTargetUser?.employeeId || ''}
              fullWidth
              disabled
            />
            <TextField
              label="New hire date"
              type="date"
              value={rehireHireDate}
              onChange={(e) => setRehireHireDate(e.target.value)}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={rehireMustChangePassword}
                  onChange={(e) => setRehireMustChangePassword(e.target.checked)}
                />
              }
              label="Require password change on next login"
            />
            <Typography variant="body2" color="text.secondary">
              History is kept; schedules are not auto-restored. Review device assignment.
              TEKS login/access will be restored with the new hire date.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRestoreFlow} disabled={restoreSubmitting}>Cancel</Button>
          <Button
            onClick={handleConfirmRehire}
            variant="contained"
            disabled={restoreSubmitting || !rehireHireDate}
            startIcon={restoreSubmitting ? <CircularProgress size={18} /> : null}
          >
            {restoreSubmitting ? 'Rehiring…' : 'Rehire'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onClose={handleCloseResetPasswordDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Reset Password for {resetPasswordUser?.firstName} {resetPasswordUser?.lastName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="New Password"
              type={showResetPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              required
              helperText="Password must be at least 6 characters long"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleToggleResetPasswordVisibility}
                      edge="end"
                    >
                      {showResetPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetPasswordDialog}>Cancel</Button>
          <Button onClick={handleResetPassword} variant="contained" color="warning">
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UsersManagementPage;
